import { camelCase, snakeCase } from 'lodash';
import {
  type CamelCasedPropertiesDeep,
  type SnakeCasedPropertiesDeep,
} from 'type-fest';

import { TO_NOT_TRANSFORM, TO_ALWAYS_TRANSFORM } from './constants';

type SyntaxModifier = (str: string) => string;

interface AxiosConfig {
  [key: string]: unknown;
  data?: unknown;
  params?: unknown;
}

interface AxiosResponse {
  [key: string]: unknown;
  data: unknown;
}

export const configToSnakeCase = <T extends AxiosConfig>(
  config: T,
): SnakeCasedPropertiesDeep<T> => {
  const { params, data } = config;

  return {
    ...config,
    params: keysSnakeCaseModifier(params),
    data: keysSnakeCaseModifier(data),
  } as SnakeCasedPropertiesDeep<T>;
};

export const responseToCamelCase = <T extends AxiosResponse>(
  response: T,
): CamelCasedPropertiesDeep<T> => {
  const { data } = response;

  return {
    ...response,
    data: keysCamelCaseModifier(data),
  } as CamelCasedPropertiesDeep<T>;
};

// https://hivebrite.atlassian.net/browse/PS-11494
// custom fields have other prefixes that needed to be included in this regex
const isCustomAttribute = (key: string): boolean =>
  /^(gte_|lte_|min__|max__|_).*/.test(key);

const shouldSkipKeySyntaxModifier = (key: string): boolean => {
  if (TO_ALWAYS_TRANSFORM.includes(key)) {
    return false;
  }

  return isCustomAttribute(key) || TO_NOT_TRANSFORM.includes(key);
};

export const keysCamelCaseModifier = <T>(
  object: T,
): CamelCasedPropertiesDeep<T> =>
  keysSyntaxModifier(camelCase, object) as CamelCasedPropertiesDeep<T>;

export const keysSnakeCaseModifier = <T>(
  object: T,
): SnakeCasedPropertiesDeep<T> =>
  keysSyntaxModifier(snakeCase, object) as SnakeCasedPropertiesDeep<T>;

const keysSyntaxModifier = (
  syntaxModifier: SyntaxModifier,
  object: unknown,
): unknown => {
  // Recursive function to pass through each nested object to change keys syntax
  const appliedSyntaxToObject = (obj: unknown): unknown => {
    if (Array.isArray(obj)) {
      return obj.map((v) => appliedSyntaxToObject(v));
    }
    if (isFormData(obj)) {
      const modifiedFormData = new FormData();

      // Cast to FormData since we've already checked it's a FormData instance
      const formData = obj as FormData;

      formData.forEach((value, key) => {
        modifiedFormData.append(
          syntaxModifier(key),
          keysSyntaxModifier(syntaxModifier, value) as File | string,
        );
      });

      return modifiedFormData;
    }
    if (isADefinedObject(obj)) {
      return Object.keys(obj as Record<string, unknown>).reduce(
        (result, key) => ({
          ...result,
          [shouldSkipKeySyntaxModifier(key) ? key : syntaxModifier(key)]:
            appliedSyntaxToObject((obj as Record<string, unknown>)[key]),
        }),
        {},
      );
    }

    return obj;
  };

  return appliedSyntaxToObject(object);
};

const isADefinedObject = (object: unknown): object is Record<string, unknown> =>
  object !== null &&
  object !== undefined &&
  (object as any)?.constructor === Object;

const isFormData = (object: unknown): object is FormData =>
  (object as any)?.constructor === FormData;
